import { Component, input, linkedSignal } from "@angular/core";

/**
 * A thumbnail cell for an attachment photo.
 *
 * Binds the URL straight into an img so the browser fetches it: in parallel with the other cells, only once it
 * is near the viewport, and out of its own cache on the way back. Nothing here downloads image bytes in script -
 * doing that serialises the requests and blocks the surrounding table from rendering.
 *
 * The width and height are declared so a row keeps its size while its image is still loading, instead of the
 * grid reflowing as images trickle in.
 */
@Component({
    selector: "app-attachment-photo",
    standalone: true,
    template: `
        <img
            [alt]="alt()"
            [attr.decoding]="'async'"
            [attr.height]="height()"
            [attr.loading]="eager() ? 'eager' : 'lazy'"
            [attr.width]="width()"
            [src]="displaySrc()"
            (error)="onError()"
        />
    `,
    styles: [
        `
            :host {
                display: inline-block;
            }

            img {
                object-fit: contain;
                max-width: 100%;
            }
        `,
    ],
})
export class AttachmentPhotoComponent {
    /** Shown when the image cannot be loaded, so a broken attachment doesn't render as a broken image icon. */
    static readonly fallbackSrc = "/assets/img/empty-preview.png";

    readonly src = input.required<string>();
    readonly alt = input("");
    readonly width = input(112);
    readonly height = input(80);

    /** Set for images that are visible immediately and worth loading up front, such as a galleria's main view. */
    readonly eager = input(false);

    /**
     * What the img actually points at. Linked rather than plain so that a row reused for another attachment
     * drops back to that attachment's image instead of keeping the previous one's fallback.
     */
    protected readonly displaySrc = linkedSignal(() => this.src());

    protected onError() {
        if (this.displaySrc() === AttachmentPhotoComponent.fallbackSrc) return;
        this.displaySrc.set(AttachmentPhotoComponent.fallbackSrc);
    }
}
